import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import { getFileUrl } from '../utils/fileUtils';
import { FaBriefcase, FaMapMarkerAlt, FaClock, FaCheckCircle, FaTimesCircle, FaHourglassHalf } from 'react-icons/fa';
import { toast } from 'react-toastify';

const MyApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data } = await API.get('/applications/my');
      console.log('📋 Total Applications:', data.applications.length);
      console.log('📋 Applications with resumes:', data.applications.filter(a => a.resume).length);
      console.log('📋 Sample data:', data.applications.slice(0, 2).map(a => ({ 
        id: a._id, 
        resume: a.resume,
        hasResume: !!a.resume,
        jobTitle: a.job?.title 
      })));
      setApplications(data.applications);
    } catch (error) {
      toast.error('Failed to fetch applications');
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Applied':
        return <FaHourglassHalf className="text-blue-500" />;
      case 'Under Review':
        return <FaClock className="text-yellow-500" />;
      case 'Shortlisted':
        return <FaCheckCircle className="text-green-500" />;
      case 'Rejected':
        return <FaTimesCircle className="text-red-500" />;
      case 'Accepted':
        return <FaCheckCircle className="text-green-600" />;
      default:
        return <FaHourglassHalf className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Applied':
        return 'bg-blue-100 text-blue-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Shortlisted':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Accepted':
        return 'bg-green-200 text-green-900';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredApplications = filter === 'all' 
    ? applications 
    : applications.filter(app => app.status === filter);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-t-2 border-[#00c2ab]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-20">
      {/* Premium Dashboard Header */}
      <div className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] pt-16 pb-32 overflow-hidden">
        {/* Abstract Background Blurs */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-white opacity-10 blur-3xl mix-blend-overlay"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 rounded-full bg-white opacity-10 blur-2xl mix-blend-overlay"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight drop-shadow-md">My Applications</h1>
          <p className="text-lg text-teal-50 font-medium">Track and manage your job applications in one place</p>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-20 relative z-20">
        {/* Elevated Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <div className="bg-gradient-to-br from-[#00c2ab] to-[#009688] rounded-[1.5rem] shadow-[0_15px_30px_rgba(0,194,171,0.2)] p-6 text-white transform transition-all duration-300 hover:-translate-y-1">
            <div className="text-3xl font-extrabold mb-1">{applications.length}</div>
            <div className="text-sm font-medium text-teal-50 uppercase tracking-wider">Total Applied</div>
          </div>
          <div className="bg-white rounded-[1.5rem] shadow-lg p-6 border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <div className="text-3xl font-extrabold text-blue-600 mb-1">
              {applications.filter(a => a.status === 'Applied').length}
            </div>
            <div className="text-sm font-bold text-gray-500 uppercase tracking-wider">Applied</div>
          </div>
          <div className="bg-white rounded-[1.5rem] shadow-lg p-6 border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <div className="text-3xl font-extrabold text-yellow-500 mb-1">
              {applications.filter(a => a.status === 'Under Review').length}
            </div>
            <div className="text-sm font-bold text-gray-500 uppercase tracking-wider">Under Review</div>
          </div>
          <div className="bg-white rounded-[1.5rem] shadow-lg p-6 border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <div className="text-3xl font-extrabold text-green-500 mb-1">
              {applications.filter(a => a.status === 'Shortlisted').length}
            </div>
            <div className="text-sm font-bold text-gray-500 uppercase tracking-wider">Shortlisted</div>
          </div>
          <div className="bg-white rounded-[1.5rem] shadow-lg p-6 border border-gray-100 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <div className="text-3xl font-extrabold text-red-500 mb-1">
              {applications.filter(a => a.status === 'Rejected').length}
            </div>
            <div className="text-sm font-bold text-gray-500 uppercase tracking-wider">Rejected</div>
          </div>
        </div>

        {/* Sleek Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-md p-2 mb-8 inline-flex flex-wrap gap-2 border border-gray-100">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
              filter === 'all' 
                ? 'bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white shadow-md' 
                : 'bg-transparent text-gray-600 hover:bg-teal-50 hover:text-[#00c2ab]'
            }`}
          >
            All ({applications.length})
          </button>
          <button
            onClick={() => setFilter('Applied')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
              filter === 'Applied' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-transparent text-gray-600 hover:bg-blue-50 hover:text-blue-600'
            }`}
          >
            Applied ({applications.filter(a => a.status === 'Applied').length})
          </button>
          <button
            onClick={() => setFilter('Under Review')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
              filter === 'Under Review' 
                ? 'bg-yellow-500 text-white shadow-md' 
                : 'bg-transparent text-gray-600 hover:bg-yellow-50 hover:text-yellow-600'
            }`}
          >
            Under Review ({applications.filter(a => a.status === 'Under Review').length})
          </button>
          <button
            onClick={() => setFilter('Shortlisted')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
              filter === 'Shortlisted' 
                ? 'bg-green-500 text-white shadow-md' 
                : 'bg-transparent text-gray-600 hover:bg-green-50 hover:text-green-600'
            }`}
          >
            Shortlisted ({applications.filter(a => a.status === 'Shortlisted').length})
          </button>
          <button
            onClick={() => setFilter('Rejected')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
              filter === 'Rejected' 
                ? 'bg-red-500 text-white shadow-md' 
                : 'bg-transparent text-gray-600 hover:bg-red-50 hover:text-red-600'
            }`}
          >
            Rejected ({applications.filter(a => a.status === 'Rejected').length})
          </button>
        </div>

        {/* Applications List */}
        <div className="space-y-6">
          {filteredApplications.length > 0 ? (
            filteredApplications.map((application) => (
              <div key={application._id} className="bg-white rounded-[2rem] shadow-sm hover:shadow-[0_15px_40px_rgba(0,194,171,0.12)] border border-gray-100 transition-all duration-300 overflow-hidden group">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    {/* Job Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-5">
                        {/* Company Avatar */}
                        <div className="flex-shrink-0">
                          {application.job?.company?.logo ? (
                            <>
                              <img
                                src={getFileUrl(`uploads/${application.job.company.logo}`)}
                                alt={application.job?.company?.name}
                                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-contain bg-white shadow-md border border-gray-100 p-2 transform group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="hidden w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl items-center justify-center text-[#00c2ab] text-3xl font-extrabold shadow-sm border border-teal-200">
                                {application.job?.company?.name?.charAt(0)}
                              </div>
                            </>
                          ) : (
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl flex items-center justify-center text-[#00c2ab] text-3xl font-extrabold shadow-sm border border-teal-200 transform group-hover:scale-105 transition-transform duration-300">
                              {application.job?.company?.name?.charAt(0)}
                            </div>
                          )}
                        </div>

                        {/* Job Details */}
                        <div className="flex-1 pt-1">
                          <Link
                            to={`/jobs/${application.job?._id}`}
                            className="text-2xl font-bold text-gray-900 hover:text-[#00c2ab] transition-colors line-clamp-1 mb-1"
                          >
                            {application.job?.title}
                          </Link>
                          <p className="text-lg text-gray-600 font-medium mb-4">{application.job?.company?.name}</p>
                          
                          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-gray-600 mb-5">
                            <span className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 font-medium">
                              <FaMapMarkerAlt className="text-[#00c2ab]" />
                              {application.job?.location?.city && application.job?.location?.state
                                ? `${application.job.location.city}, ${application.job.location.state}`
                                : 'Location not specified'}
                            </span>
                            <span className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 font-medium">
                              <FaBriefcase className="text-[#00c2ab]" />
                              {application.job?.employmentType}
                            </span>
                            <span className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 font-medium">
                              <FaClock className="text-[#00c2ab]" />
                              Applied {formatDate(application.appliedAt)}
                            </span>
                          </div>

                          {/* Status Badge */}
                          <div className="flex items-center">
                            <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold shadow-sm ${getStatusColor(application.status)}`}>
                              {getStatusIcon(application.status)}
                              {application.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-3 min-w-[160px] pt-2">
                      <Link
                        to={`/jobs/${application.job?._id}`}
                        className="px-6 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-bold text-center hover:border-[#00c2ab] hover:text-[#00c2ab] transition-all duration-300"
                      >
                        View Job
                      </Link>
                      {application.resume ? (
                        <a
                          href={getFileUrl(application.resume)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-2.5 rounded-xl border-2 border-[#00c2ab] bg-teal-50 text-[#00c2ab] font-bold text-center hover:bg-[#00c2ab] hover:text-white shadow-[0_4px_14px_rgba(0,194,171,0.2)] transition-all duration-300"
                          onClick={(e) => {
                            console.log('🔍 Resume path:', application.resume);
                            console.log('🔗 Full URL:', e.currentTarget.href);
                          }}
                        >
                          View Resume
                        </a>
                      ) : (
                        <span
                          className="px-6 py-2.5 rounded-xl border-2 border-gray-100 bg-gray-50 text-gray-400 font-bold text-center cursor-not-allowed"
                          title="No resume was uploaded with this application"
                        >
                          No Resume
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Cover Letter Preview */}
                  {application.coverLetter && (
                    <div className="mt-6 pt-5 border-t border-gray-100">
                      <p className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">Cover Letter</p>
                      <p className="text-gray-600 bg-gray-50 p-4 rounded-xl border border-gray-100 leading-relaxed italic text-sm">
                        "{application.coverLetter}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-[2rem] shadow-xl p-16 text-center border border-gray-100 transform transition-all hover:-translate-y-1 hover:shadow-2xl">
              <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FaBriefcase className="text-4xl text-[#00c2ab]" />
              </div>
              <h3 className="text-3xl font-extrabold text-gray-900 mb-3">
                {filter === 'all' ? 'No Applications Yet' : `No ${filter} Applications`}
              </h3>
              <p className="text-xl text-gray-500 mb-8 max-w-md mx-auto">
                {filter === 'all' 
                  ? "You haven't applied to any jobs yet. Start exploring opportunities to build your career." 
                  : `You don't have any applications currently marked as ${filter}.`}
              </p>
              {filter === 'all' && (
                <Link to="/jobs" className="inline-block bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white px-10 py-4 rounded-xl font-bold shadow-[0_10px_25px_rgba(0,194,171,0.3)] hover:shadow-[0_15px_35px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300">
                  Browse Available Jobs
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyApplications;
